/**
 * Name normalization for entity resolution.
 *
 * Two organizations are compared by their names as filers wrote them, and
 * filers write the same organization a dozen ways. Every comparison in the
 * matcher goes through the same three views of a name: the normalized form
 * (case, accents, punctuation, and whitespace folded, the ampersand spelled
 * out), the core form (corporate suffixes and articles removed, so "A New
 * Leaf Inc" and "New Leaf" agree), and the distinctive tokens (the words of
 * the core form that carry identity). Governments are recognized here too,
 * because a city, a county, or a state university is not in the exempt
 * organization master file and must not be forced onto something that is.
 */
const COMBINING_MARKS = /[̀-ͯ]/g;
const APOSTROPHES = /['\u2019]/g;

export const normalizeName = (name: string): string =>
  name
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    // An apostrophe is removed rather than treated as a separator, so that a
    // possessive stays one word: the master file writes "ROSIES HOUSE" and a
    // filer writes "Rosie's House", and splitting would leave a stray "S"
    // that no rule could join. The identifier module folds it the same way.
    .replace(APOSTROPHES, '')
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

// Only legal form markers and articles are dropped. Words like Foundation,
// Fund, Trust, and Association carry identity: a university and its
// foundation are two organizations with two numbers, and dropping the word
// joined "Northern Arizona University" to the university's foundation in four
// grants of the fixture sample. The earlier list dropped them and matched
// more names at the cost of matching the wrong ones.
export const SUFFIXES: ReadonlySet<string> = new Set(['INC', 'INCORPORATED', 'LLC', 'LTD', 'CORP', 'CORPORATION', 'CO', 'COMPANY', 'THE', 'A', 'AN', 'OF', 'FOR', 'AND']);

// The master file abbreviates where a filer spells out. Expanding is safer
// than dropping: it joins the two spellings of one word without letting two
// different words fall together.
const ABBREVIATIONS = new Map([
  ['FDN', 'FOUNDATION'],
  ['ASSN', 'ASSOCIATION'],
  ['ASSOC', 'ASSOCIATION'],
  ['INTL', 'INTERNATIONAL'],
  ['NATL', 'NATIONAL'],
  ['AMER', 'AMERICAN'],
  ['UNIV', 'UNIVERSITY'],
  ['CTR', 'CENTER'],
  ['CNTR', 'CENTER'],
  ['CENTRE', 'CENTER'],
  ['SOC', 'SOCIETY'],
  ['HOSP', 'HOSPITAL'],
  ['DEPT', 'DEPARTMENT'],
  ['ST', 'SAINT'],
  ['STE', 'SAINTE'],
  ['MT', 'MOUNT'],
]);

/**
 * The name with legal forms and articles dropped and abbreviations spelled
 * out; the whole name when nothing else is left. "St" is read as "Saint"
 * because the master file writes both and a street never appears in an
 * organization's name where a saint could be meant.
 */
export const coreName = (name: string): string => {
  const normalized = normalizeName(name);
  const kept = normalized
    .split(' ')
    .filter((word) => !SUFFIXES.has(word))
    .map((word) => ABBREVIATIONS.get(word) ?? word);
  return kept.length === 0 ? normalized : kept.join(' ');
};

/** Words of the core name that can identify an organization: two letters or more. */
export const tokens = (name: string): string[] => coreName(name).split(' ').filter((word) => word.length >= 2);

// A government body is named as the body itself, not as something of it. The
// patterns below are anchored at both ends wherever the shape allows, because
// an unanchored "COUNTY" or "PUBLIC LIBRARY" matches the charities named after
// the place they serve: Habitat for Humanity of Pima County, the Phoenix
// Public Library Foundation, and the Prescott Unified School District
// Education Foundation are exempt organizations with their own numbers, and an
// earlier version of these patterns refused all three.
const GOVERNMENT_PATTERNS = [
  /^(CITY|TOWN|VILLAGE|COUNTY|STATE) OF [A-Z ]+$/,
  /^[A-Z]+(?: [A-Z]+){0,2} COUNTY$/,
  /^[A-Z ]+ (SCHOOL DISTRICT|UNIFIED DISTRICT|ELEMENTARY DISTRICT|PUBLIC LIBRARY|PUBLIC SCHOOLS?|COMMUNITY COLLEGE DISTRICT)( NO \d+| #\d+)?$/,
  /^[A-Z]+ STATE UNIVERSITY$/,
  /^UNIVERSITY OF [A-Z]+$/,
];

// A name that carries one of these words names an organization of its own,
// whatever place it is named after. The check runs after the patterns, so a
// school district's education foundation is an organization and the district
// itself is not.
const OWN_ORGANIZATION_WORDS =
  /\b(FOUNDATION|FRIENDS|SOCIETY|CLUBS?|ASSOCIATION|ALLIANCE|MUSEUMS?|YMCA|YWCA|HABITAT|UNITED WAY|BOOSTERS?|AUXILIARY|PARTNERSHIP|COALITION|INC|INCORPORATED)\b/;

export const isGovernmentName = (name: string): boolean => {
  const normalized = normalizeName(name);
  return !OWN_ORGANIZATION_WORDS.test(normalized) && GOVERNMENT_PATTERNS.some((pattern) => pattern.test(normalized));
};
